import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import '@diekeure/dcr-components/dcr-app-shell.js';
import '@diekeure/dcr-components/dcr-inline-button.js';
import { combineLatest, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { AppShellUIService } from './services/app-shell.service';

interface ShellConfig {
  header?: boolean;
  navigationRail?: boolean;
  mobileNavigationRail?: boolean;
  navigationDrawer?: boolean;
  mobileNavigationDrawer?: boolean;
  pageHeader?: boolean;
  pageFooter?: boolean;
  sideSheet?: boolean;
  mobileSideSheet?: boolean;
  footer?: boolean;
}

@Component({
  standalone: false,
  selector: 'campus-app-shell',
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      state('void', style({ opacity: 0 })),
      state('*', style({ opacity: 1 })),
      transition(':enter', animate('200ms cubic-bezier(0.2, 0, 0, 1)')),
      transition(':leave', animate('200ms cubic-bezier(0.2, 0, 0, 1)')),
    ]),
  ],
})
export class AppShellComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _appShellService = inject(AppShellUIService);
  private _destroy$ = new Subject<void>();
  private _cdRef = inject(ChangeDetectorRef);

  public config: ShellConfig = {};
  public visibleItemsConfig$ = this._appShellService.visibleItemsConfig$;
  public highlightItemsConfig$ = this._appShellService.highlightItemsConfig$;

  public openMenu$ = this._appShellService.openMenu$.asObservable();
  public isTablet$ = this._appShellService.isTablet$;

  private _navRailByData = false;
  private _navDrawerByData = false;

  private _sideSheetByData = false;

  private _currentPrimaryRoot: string | null = null;
  private _hadDrawerInPrimary = false;

  @HostBinding('class') readonly defaultClasses = ['block', 'w-full', 'h-full'];

  ngOnInit(): void {
    this.config = this._getInitialConfig();
    this._setConfigForRoute(this._route);

    // Ensure the primary root is set before first isDesktop emission
    this._currentPrimaryRoot = this._getPrimaryRootSegment(this._router.url);

    this._listenToRouteChanges();

    combineLatest([this._appShellService.isDesktop$, this._appShellService.isTablet$])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([isDesktop, isTablet]) => {
        if (isDesktop) this._setForDesktop();
        else if (isTablet) this._setForTablet();
        else this._setForMobile();
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public skipToContent(): void {
    document.querySelector('main')?.focus();
  }

  public closeMenu() {
    this._appShellService.closeMenu();
  }

  public closeSideSheet() {
    this._appShellService.setShowSideSheet(false);
  }

  public navToPrimary() {
    if (this.config.mobileNavigationRail && this.config.navigationDrawer) {
      this._appShellService.setShowNavigationRail(true);
      this._appShellService.setShowNavigationDrawer(false);
    }
  }

  private _listenToRouteChanges(): void {
    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this._destroy$),
        switchMap(() => combineLatest([this._appShellService.isDesktop$, this._appShellService.isTablet$]))
      )
      .subscribe(([isDesktop, isTablet]) => {
        document
          .querySelector('dcr-app-shell')
          ?.shadowRoot.querySelector('main')
          ?.scrollTo({ left: 0, top: 0, behavior: 'smooth' });

        // Recompute config from route data/outlets
        this.config = this._getInitialConfig();
        this._setConfigForRoute(this._route);

        // Detect primary root changes and reset the "had drawer" flag per group
        const currentRoot = this._getPrimaryRootSegment(this._router.url);
        if (this._currentPrimaryRoot !== currentRoot) {
          this._currentPrimaryRoot = currentRoot;
          this._hadDrawerInPrimary = false;
        }

        if (isDesktop) this._setForDesktop();
        else if (isTablet) this._setForTablet();
        else this._setForMobile();
      });
  }

  private _setForDesktop() {
    // NOTE: _sideSheetByData is treated as the route’s default open state.
    // The final value will be: prefFromStorage ?? _sideSheetByData ?? default

    this.config = {
      ...this.config,
      navigationRail: true,
      mobileNavigationRail: false,
      mobileNavigationDrawer: false,
      navigationDrawer: !!this._navDrawerByData,
      mobileSideSheet: false,
    };
    this._appShellService.setShowNavigationRail(true);
    this._appShellService.setShowNavigationDrawer(true);

    this._applySideSheetPreference({ isDesktop: true });
    this._cdRef.markForCheck();
  }

  private _setForTablet() {
    this.config = {
      ...this.config,
      navigationRail: false,

      mobileNavigationRail: !!this._navRailByData,
      mobileNavigationDrawer: false,
      navigationDrawer: !!this._navDrawerByData,
      mobileSideSheet: this.config.sideSheet,
    };
    this._appShellService.openMenu();

    const hasRail = !!this.config.mobileNavigationRail;
    if (hasRail) {
      this._appShellService.setShowNavigationRail(false);
      this.closeMenu();
    }

    this._appShellService.setShowNavigationDrawer(true);

    this._applySideSheetPreference({ isDesktop: false });
    this._cdRef.markForCheck();
  }

  private _setForMobile() {
    this.config = {
      ...this.config,
      navigationRail: false,
      navigationDrawer: false,
      mobileNavigationRail: !!this._navRailByData,
      mobileNavigationDrawer: !!this._navDrawerByData,
      mobileSideSheet: this.config.sideSheet,
    };

    const hasRail = !!this.config.mobileNavigationRail;
    const hasDrawer = !!this.config.mobileNavigationDrawer;

    if (hasRail && hasDrawer) {
      this._appShellService.setShowNavigationRail(false);
      this._appShellService.setShowNavigationDrawer(true);

      if (this._hadDrawerInPrimary) {
        this.closeMenu();
      } else {
        this._hadDrawerInPrimary = true;
      }
    } else if (hasRail && !hasDrawer) {
      this._appShellService.setShowNavigationRail(true);
      this._appShellService.setShowNavigationDrawer(false);
      this.closeMenu();
    } else {
      this._appShellService.setShowNavigationRail(false);
      this._appShellService.setShowNavigationDrawer(false);
      this.closeMenu();
    }

    this._applySideSheetPreference({ isDesktop: false });
    this._cdRef.markForCheck();
  }

  private _applySideSheetPreference(opts: { isDesktop: boolean }) {
    // Ensure root is set (on very first render it could be '')
    if (!this._currentPrimaryRoot) {
      this._currentPrimaryRoot = this._getPrimaryRootSegment(this._router.url);
    }

    const root = this._currentPrimaryRoot || '';
    const mode = opts.isDesktop ? 'desktop' : 'mobile';
    const defaultValue = opts.isDesktop ? true : false;

    // 1) Load preference from storage (if any)
    const stored = root ? this._appShellService.getSideSheetPreference(root, mode) : null;

    // 2) If there is a stored preference, copy it into _sideSheetByData so component state reflects persisted choice
    if (stored !== null) {
      this._sideSheetByData = stored;
    }

    // 3) Compute the next visibility:
    //    stored preference wins, then route default (_sideSheetByData), then generic default per mode

    const next = (stored ?? this._sideSheetByData ?? defaultValue) === true;

    this._appShellService.setShowSideSheet(next);
  }

  private _setConfigForRoute(route: ActivatedRoute): void {
    if (route.children.length === 0) return;

    for (const child of route.children) {
      this._setByRouteOutlet(child);
      this._setByRouteData(child);
      this._setConfigForRoute(child);
    }

    this._navRailByData = !!this.config.navigationRail;
    this._navDrawerByData = !!this.config.navigationDrawer;

    // Treat this.config.sideSheet as the route’s default open state (not a hard availability flag)
    // It will be overridden by persisted preference (if any) in _applySideSheetPreference.
    this._sideSheetByData = this.config.sideSheet ?? this._sideSheetByData;
  }

  private _setByRouteOutlet(route: ActivatedRoute): void {
    if (route.outlet !== 'primary') {
      const key = this._getConfigKeyFromOutlet(route.outlet);
      if (key) this.config[key] = this.config[key] === undefined || this.config[key];
    }
  }

  private _setByRouteData(route: ActivatedRoute): void {
    const { data } = route.snapshot;

    for (const [key, value] of Object.entries(data)) {
      if (key in this.config && typeof value === 'boolean') {
        this.config[key as keyof ShellConfig] = value;
      }
    }
  }

  private _getInitialConfig(): ShellConfig {
    return {
      header: undefined,
      navigationRail: undefined,
      mobileNavigationRail: undefined,
      navigationDrawer: undefined,
      mobileNavigationDrawer: undefined,
      pageHeader: undefined,
      pageFooter: undefined,
      sideSheet: undefined,
      mobileSideSheet: undefined,
      footer: undefined,
    };
  }

  private _getConfigKeyFromOutlet(outlet: string): keyof ShellConfig | null {
    const mapping: Record<string, keyof ShellConfig> = {
      header: 'header',
      'navigation-rail': 'navigationRail',
      'mobile-navigation-rail': 'mobileNavigationRail',
      'navigation-drawer': 'navigationDrawer',
      'page-header': 'pageHeader',
      'page-footer': 'pageFooter',
      'side-sheet': 'sideSheet',
      'mobile-side-sheet': 'mobileSideSheet',
      footer: 'footer',
    };
    return mapping[outlet] || null;
  }

  private _getPrimaryRootSegment(url: string): string {
    try {
      const tree = this._router.parseUrl(url);
      const primary = tree.root.children['primary'];
      if (!primary || primary.segments.length === 0) return '';
      const p = primary.segments[0].path === 'v2' ? primary.segments[1]?.path : primary.segments[0].path;
      return p || '';
    } catch {
      return '';
    }
  }
}
